import { DomainEventPayload } from '../events/event.types';
import {
  CreatorEconomyLoop,
  CreatorEconomyLoopSnapshot,
} from '../loop/creator-economy-loop';
import { EventProcessingPipelineEnvelope } from './event-processing-pipeline';
import { FeedImpactUpdate } from './feed-impact-update.contract';
import { FeedGenerationContract, FeedItemRankOrder } from './feed-generation.contract';
import { LifecycleEvaluationResult } from './lifecycle-evaluation.contract';
import { ScoreUpdateInstruction } from './score-update.contract';

/** Responsibilities of the execution engine (design contract, not runtime). */
export const CREATOR_ECONOMY_EXECUTION_ENGINE_RESPONSIBILITIES = [
  'consume domain events',
  'update creator scores',
  'update lifecycle state',
  'update feed impact inputs',
] as const;

/** Input to a single deterministic execution cycle. */
export interface CreatorEconomyExecutionInput {
  creatorId: string;
  event: DomainEventPayload;
  loop: CreatorEconomyLoop;
}

/** Output of a single deterministic execution cycle. */
export interface CreatorEconomyExecutionOutput {
  creatorId: string;
  pipeline: EventProcessingPipelineEnvelope;
  scoreUpdates: ScoreUpdateInstruction[];
  feedImpactUpdates: FeedImpactUpdate[];
  lifecycleEvaluation: LifecycleEvaluationResult | null;
  updatedLoop: CreatorEconomyLoopSnapshot;
  feedRankOrder: FeedItemRankOrder | null;
}

/**
 * CreatorEconomyExecutionEngine — domain-level execution contract.
 * Defines how events, scores, lifecycle, and feed inputs update each other.
 * Implementation is deferred; this interface is the runtime design boundary.
 */
export interface CreatorEconomyExecutionEngine {
  readonly responsibilities: typeof CREATOR_ECONOMY_EXECUTION_ENGINE_RESPONSIBILITIES;
  execute(input: CreatorEconomyExecutionInput): CreatorEconomyExecutionOutput;
  generateFeed(contract: FeedGenerationContract): FeedItemRankOrder;
}

/** Documents deterministic update order inside the engine. */
export const CREATOR_ECONOMY_EXECUTION_ORDER = [
  'receive DomainEvent',
  'resolve EventLoopImpactMapping',
  'emit ScoreUpdateInstruction(s)',
  'emit FeedImpactUpdate(s)',
  'evaluate LifecycleTriggerRules',
  'persist CreatorEconomyLoopSnapshot',
  'optionally invoke FeedGenerationContract',
] as const;
